'use client';

import { useAuth } from '@clerk/clerk-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui';
import {
  type Attempt,
  ATTEMPT_ACTIVITY_EXPORT_STATES,
  type AttemptActivity,
  getAttemptActivityExport,
  readAttemptActivityExport,
  requestAttemptActivityExport,
} from '@/lib/admin/client';
import {
  attemptActivityPages,
  useAttemptActivities,
} from '@/lib/query/useAttemptActivities';

import type { BadgeVariant } from './badgeVariants';
import { formatRunDuration, taskAttemptToTrace } from './traceAdapters';
import { TraceDrilldown, type TraceEvent } from './TraceDrilldown';

const ATTEMPT_ACTIVITY_TYPES = {
  CLAIMED: 'claimed',
  IN_PROGRESS: 'in_progress',
  SUBMITTED: 'submitted',
  ACCEPTED: 'accepted',
  RETRY_REQUIRED: 'retry_required',
  LEASE_RECLAIMED: 'lease_reclaimed',
  CAPTURE_DEGRADED: 'capture_degraded',
  CAPTURE_CAPABILITIES: 'capture_capabilities_observed',
  PROVIDER_EXITED: 'provider_exited',
} as const;

const ATTEMPT_STATE_ACTIVITY_TYPES = new Set<string>([
  ATTEMPT_ACTIVITY_TYPES.CLAIMED,
  ATTEMPT_ACTIVITY_TYPES.IN_PROGRESS,
  ATTEMPT_ACTIVITY_TYPES.SUBMITTED,
  ATTEMPT_ACTIVITY_TYPES.ACCEPTED,
  ATTEMPT_ACTIVITY_TYPES.RETRY_REQUIRED,
  ATTEMPT_ACTIVITY_TYPES.LEASE_RECLAIMED,
]);

const ACTIVITY_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  [ATTEMPT_ACTIVITY_TYPES.CLAIMED]: 'success',
  [ATTEMPT_ACTIVITY_TYPES.IN_PROGRESS]: 'info',
  [ATTEMPT_ACTIVITY_TYPES.SUBMITTED]: 'warning',
  [ATTEMPT_ACTIVITY_TYPES.ACCEPTED]: 'green',
  [ATTEMPT_ACTIVITY_TYPES.RETRY_REQUIRED]: 'orange',
  [ATTEMPT_ACTIVITY_TYPES.LEASE_RECLAIMED]: 'secondary',
  [ATTEMPT_ACTIVITY_TYPES.CAPTURE_CAPABILITIES]: 'info',
};

const ACTIVITY_OUTCOME_VARIANTS: Record<string, BadgeVariant> = {
  started: 'info',
  succeeded: 'green',
  failed: 'error',
  canceled: 'orange',
  degraded: 'error',
  observed: 'outline',
};

const ATTEMPT_HEADER_METADATA = new Set(['State', 'Conclusion', 'Attempt']);
const ATTEMPT_STATE_CONTEXT = 'Attempt state changed';
const ATTEMPT_ACTIVITY_EXPORT_POLL_INTERVAL_MS = 1_000;
const ATTEMPT_ACTIVITY_EXPORT_POLL_LIMIT = 60;
const ATTEMPT_ACTIVITY_EXPORT_MEDIA_TYPE = 'application/json';

const ACTIVITY_PAYLOAD_FIELDS = {
  PROVIDER: 'provider',
  MODE: 'mode',
  CAPABILITY_GAPS: 'capabilityGaps',
  NAME: 'name',
  PHASE: 'phase',
  COMMAND_NAME: 'commandName',
  INPUT_TOKENS: 'inputTokenCount',
  OUTPUT_TOKENS: 'outputTokenCount',
  COST_USD: 'costUsd',
  DURATION_MS: 'durationMs',
  REASON: 'reason',
} as const;

function activityReason(payload: string): string {
  if (!payload) return '';
  try {
    const value: unknown = JSON.parse(payload);
    if (
      typeof value === 'object' &&
      value !== null &&
      'reason' in value &&
      typeof value.reason === 'string'
    ) {
      return value.reason;
    }
  } catch {
    // A malformed optional payload must not make the authoritative timeline unreadable.
  }
  return '';
}

function activityTitle(activityType: string): string {
  return activityType.replaceAll('_', ' ');
}

function activityBadge(activity: AttemptActivity): TraceEvent['badge'] {
  const label = activityTitle(activity.activityType);
  if (activity.activityType === ATTEMPT_ACTIVITY_TYPES.CAPTURE_DEGRADED) {
    return { label, variant: 'error' };
  }
  if (activity.redacted) {
    return { label, variant: 'orange' };
  }
  return {
    label,
    variant:
      ACTIVITY_BADGE_VARIANTS[activity.activityType] ??
      ACTIVITY_OUTCOME_VARIANTS[activity.outcome] ??
      'outline',
  };
}

function activityDetail(activity: AttemptActivity): string {
  const isStateTransition = ATTEMPT_STATE_ACTIVITY_TYPES.has(
    activity.activityType
  );
  if (isStateTransition) {
    const reason = activityReason(activity.payload);
    return reason ? `Reason: ${reason}` : '';
  }

  const payload = activityPayload(activity.payload);
  const parts: string[] = [];
  if (activity.activityType === ATTEMPT_ACTIVITY_TYPES.CAPTURE_CAPABILITIES) {
    appendPayloadString(parts, payload, ACTIVITY_PAYLOAD_FIELDS.PROVIDER);
    appendPayloadString(parts, payload, ACTIVITY_PAYLOAD_FIELDS.MODE);
    const gaps = payload[ACTIVITY_PAYLOAD_FIELDS.CAPABILITY_GAPS];
    const namedGaps = Array.isArray(gaps)
      ? gaps.filter((gap): gap is string => typeof gap === 'string')
      : [];
    if (namedGaps.length > 0) {
      parts.push(`Unavailable: ${namedGaps.join(', ').replaceAll('_', ' ')}`);
    }
  } else {
    appendPayloadString(parts, payload, ACTIVITY_PAYLOAD_FIELDS.NAME);
    appendPayloadString(parts, payload, ACTIVITY_PAYLOAD_FIELDS.PHASE);
    appendPayloadString(parts, payload, ACTIVITY_PAYLOAD_FIELDS.COMMAND_NAME);
  }
  appendUsage(parts, payload);
  appendDuration(parts, payload, activity.activityType);
  appendPayloadString(parts, payload, ACTIVITY_PAYLOAD_FIELDS.REASON);
  return parts.filter(Boolean).join(' · ');
}

function activityProvenance(activity: AttemptActivity): string {
  return `Recorded by ${activity.actor || 'system'} through ${activity.source}`;
}

function activityPayload(payload: string): Record<string, unknown> {
  if (!payload) return {};
  try {
    const parsed: unknown = JSON.parse(payload);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function appendPayloadString(
  parts: string[],
  payload: Record<string, unknown>,
  field: string
) {
  const value = payload[field];
  if (typeof value === 'string' && value.length > 0) parts.push(value);
}

function appendDuration(
  parts: string[],
  payload: Record<string, unknown>,
  activityType: string
) {
  const durationMs = payload[ACTIVITY_PAYLOAD_FIELDS.DURATION_MS];
  if (typeof durationMs === 'number') {
    const duration = formatRunDuration(durationMs);
    parts.push(
      activityType === ATTEMPT_ACTIVITY_TYPES.PROVIDER_EXITED
        ? `${duration} run time`
        : duration
    );
  }
}

function appendUsage(parts: string[], payload: Record<string, unknown>) {
  const input = payload[ACTIVITY_PAYLOAD_FIELDS.INPUT_TOKENS];
  const output = payload[ACTIVITY_PAYLOAD_FIELDS.OUTPUT_TOKENS];
  const cost = payload[ACTIVITY_PAYLOAD_FIELDS.COST_USD];
  if (typeof input === 'number' && input > 0)
    parts.push(`${input} input tokens`);
  if (typeof output === 'number' && output > 0)
    parts.push(`${output} output tokens`);
  if (typeof cost === 'number' && cost > 0) parts.push(`$${cost.toFixed(2)}`);
}

function activityContext(activity: AttemptActivity): string {
  return ATTEMPT_STATE_ACTIVITY_TYPES.has(activity.activityType)
    ? ATTEMPT_STATE_CONTEXT
    : activity.source;
}

function activityRefs(activity: AttemptActivity): TraceEvent['refs'] {
  const refs: NonNullable<TraceEvent['refs']> = [];
  if (activity.parentEventId) {
    refs.push({
      label: 'Parent event',
      href: `#activity-${activity.parentEventId}`,
    });
  }
  if (activity.traceId) {
    refs.push({ label: 'Trace', location: activity.traceId.slice(0, 8) });
  }
  if (activity.spanId) {
    refs.push({ label: 'Span', location: activity.spanId.slice(0, 8) });
  }
  return refs;
}

function waitForExportPoll(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(
      resolve,
      ATTEMPT_ACTIVITY_EXPORT_POLL_INTERVAL_MS
    );
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeout);
        reject(
          new DOMException('Attempt activity export canceled', 'AbortError')
        );
      },
      { once: true }
    );
  });
}

function downloadAttemptActivity(attemptId: string, content: string) {
  const objectUrl = URL.createObjectURL(
    new Blob([content], { type: ATTEMPT_ACTIVITY_EXPORT_MEDIA_TYPE })
  );
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `attempt-activity-${attemptId.replaceAll(/[^a-zA-Z0-9_-]/g, '-')}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export function AttemptActivityDrilldown({ attempt }: { attempt: Attempt }) {
  const { getToken } = useAuth();
  const query = useAttemptActivities(attempt.attemptId, true);
  const activities = attemptActivityPages(query.data?.pages);
  const exportAbortController = useRef<AbortController | null>(null);
  const [exportStatus, setExportStatus] = useState<
    'idle' | 'preparing' | 'failed'
  >('idle');
  const [exportError, setExportError] = useState('');

  useEffect(
    () => () => {
      exportAbortController.current?.abort();
    },
    []
  );

  async function exportActivity() {
    exportAbortController.current?.abort();
    const controller = new AbortController();
    exportAbortController.current = controller;
    setExportStatus('preparing');
    setExportError('');
    try {
      const token = await getToken();
      if (!token) throw new Error('Missing authentication token');
      let activityExport = await requestAttemptActivityExport(
        token,
        attempt.attemptId,
        crypto.randomUUID(),
        controller.signal
      );
      for (let poll = 0; poll < ATTEMPT_ACTIVITY_EXPORT_POLL_LIMIT; poll += 1) {
        if (activityExport.state === ATTEMPT_ACTIVITY_EXPORT_STATES.READY) {
          const content = await readAttemptActivityExport(
            token,
            attempt.attemptId,
            activityExport.exportId,
            controller.signal
          );
          downloadAttemptActivity(attempt.attemptId, content);
          setExportStatus('idle');
          return;
        }
        if (activityExport.state === ATTEMPT_ACTIVITY_EXPORT_STATES.FAILED) {
          throw new Error(
            activityExport.failureCode || 'Activity export failed'
          );
        }
        if (activityExport.state === ATTEMPT_ACTIVITY_EXPORT_STATES.EXPIRED) {
          throw new Error('Activity export expired before download');
        }
        await waitForExportPoll(controller.signal);
        activityExport = await getAttemptActivityExport(
          token,
          attempt.attemptId,
          activityExport.exportId,
          controller.signal
        );
      }
      throw new Error('Activity export did not finish within one minute');
    } catch (error) {
      if (controller.signal.aborted) return;
      setExportError(
        error instanceof Error ? error.message : 'Activity export failed'
      );
      setExportStatus('failed');
    } finally {
      if (exportAbortController.current === controller) {
        exportAbortController.current = null;
      }
    }
  }
  const node = useMemo(() => {
    const base = taskAttemptToTrace(attempt);
    const events = activities.map((activity) => ({
      id: `activity-${activity.activityId}`,
      sequence: activity.sequence,
      context: activityContext(activity),
      at: activity.occurredAt || activity.recordedAt,
      title: activityTitle(activity.activityType),
      detail: activityDetail(activity),
      provenance: activityProvenance(activity),
      badge: activityBadge(activity),
      refs: activityRefs(activity),
    }));
    return {
      ...base,
      metadata: base.metadata.filter(
        (entry) => !ATTEMPT_HEADER_METADATA.has(entry.label)
      ),
      events: [...(base.events ?? []), ...events],
    };
  }, [activities, attempt]);

  return (
    <div className="space-y-3">
      {query.hasNextPage && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage
              ? 'Loading activity…'
              : 'Load more activity'}
          </Button>
        </div>
      )}
      {query.isError && (
        <p role="alert" className="text-destructive text-sm">
          Attempt activity could not be loaded.
        </p>
      )}
      {!query.isLoading && !query.isError && activities.length === 0 && (
        <p className="text-muted-foreground text-sm">
          No activity for this Attempt.
        </p>
      )}
      <TraceDrilldown
        node={node}
        embedded
        traceActions={
          <div className="space-y-2">
            <Button
              data-export-attempt-activity
              type="button"
              variant="outline"
              size="sm"
              disabled={exportStatus === 'preparing'}
              onClick={() => void exportActivity()}
            >
              {exportStatus === 'preparing'
                ? 'Preparing export…'
                : 'Export trace'}
            </Button>
            {exportStatus === 'failed' && (
              <p role="alert" className="text-destructive text-sm">
                Export failed: {exportError}
              </p>
            )}
          </div>
        }
      />
    </div>
  );
}
